// =====================================================================
// commande-prete/synchro.js — Commande prête Nespart (13-10-2026)
// =====================================================================
// Passage écran ⇄ serveur (contrat CommandeDTO, SPEC §3.5). Différence avec
// la maquette : les données vivent sur le serveur. Chaque fiche garde ce qui
// a été TAPÉ (le message de saisie cite la frappe) ; on n'envoie au serveur
// que les valeurs justes, à la sortie du champ / Entrée / Scan. Une valeur
// fausse reste à l'écran, signalée, et n'est jamais envoyée : le serveur garde
// la dernière bonne, et l'envoi du mail est bloqué par l'état de la fiche.
//
// Champs « sales » (14/10) : une fiche n'envoie QUE les champs modifiés par
// l'utilisateur sur CET appareil (u.sales), remis à propre quand la valeur est
// enregistrée. Un champ propre n'est jamais envoyé — un champ vide ici
// effacerait sinon une valeur saisie sur un autre appareil — et il adopte la
// valeur que le serveur renvoie.
// Fonctions PURES : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

import { normSscc, ssccValide } from './sscc.js';
import { champs, verifChamp, formatEtat, num, aTaper, normRefChargement, verifRefChargement } from './regles.js';
import { ssccProbleme, enAttente } from './fiches.js';

// Valeur « pas envoyable » (fausse, en cours de question, pas encore validée).
export const INVALIDE = Object.freeze({ invalide: true });

// Champ local → champ du DTO / du corps PATCH.
const SERVEUR = { sscc: 'sscc', h: 'heightCm', l: 'lengthCm', w: 'widthCm', kg: 'weightG' };
const CORPS = { sscc: 'sscc', h: 'heightCm', l: 'lengthCm', w: 'widthCm', kg: 'weightKg' };

// Poids : saisi en kg (1 décimale) → stocké en grammes, même calcul que le serveur.
export const kgVersG = (kg) => (kg === null ? null : Math.round(kg * 10) * 100);
export const gVersKg = (g) => (g === null || g === undefined ? '' : aTaper(Math.round(g / 100) / 10));
const cmTexte = (n) => (n === null || n === undefined ? '' : String(n));

// Valeur à envoyer pour une mesure : null = champ vidé ; INVALIDE = rien à envoyer.
export function vouluChamp(u, k) {
  const r = verifChamp(u, k);
  if (r.manque) return null;
  if (r.msg) return INVALIDE;
  const n = num(u[k]);
  return k === 'kg' ? Math.round(n * 10) / 10 : n;   // cm entiers garantis par verifChamp
}

// SSCC à envoyer : seulement une fois VALIDÉ (Entrée, Scan, sortie du champ), lisible, sans
// doublon dans la commande et sans question ouverte (« SSCC changé… C'est voulu ? »).
export function vouluSscc(st, u) {
  const s = normSscc(u.sscc);
  if (!u.valide) return INVALIDE;
  if (!s) return null;
  if (ssccProbleme(st, u) || !ssccValide(s) || enAttente(st, u)) return INVALIDE;
  return s;
}

// ---------- Champs « sales » : modifiés par l'utilisateur sur CET appareil ----------
export function salir(u, k) {
  if (!u.sales) u.sales = new Set();
  u.sales.add(k);
}
export const estSale = (u, k) => !!u.sales && u.sales.has(k);
// Le serveur a enregistré `v` pour le champ local `k` : le champ redevient propre — sauf si
// l'utilisateur l'a encore changé depuis l'envoi (cette nouvelle frappe partira à son tour).
export function champEnregistre(st, u, k, v) {
  const actuel = k === 'sscc' ? vouluSscc(st, u) : vouluChamp(u, k);
  if (actuel === v && u.sales) u.sales.delete(k);
}

// Corps du PATCH d'une fiche : les champs SALES, justes, qui diffèrent du serveur.
//   exclure      — champs locaux à ne pas envoyer (celui qu'on est en train de taper) ;
//   acquis       — dernières valeurs acceptées par le serveur (corps PATCH) : une valeur que le
//                  serveur aurait normalisée autrement ne repart pas en boucle ;
//   serveurUnits — unités du DTO : un SSCC encore porté par une AUTRE unité côté serveur
//                  (elle va le perdre juste après) attend son tour au lieu de prendre un 409.
// Rend { body, differes, egaux } (differes = champs justes qui attendent leur tour ; egaux = champs
// sales déjà identiques au serveur : rien à envoyer, ils redeviennent propres).
export function diffFiche(st, u, su, { exclure = [], acquis = {}, serveurUnits = [] } = {}) {
  const body = {}, differes = [], egaux = [];
  if (!su) return { body, differes, egaux };
  for (const k of ['sscc', ...champs(u)]) {
    if (exclure.includes(k) || !estSale(u, k)) continue;
    const v = k === 'sscc' ? vouluSscc(st, u) : vouluChamp(u, k);
    if (v === INVALIDE) continue;
    const serveur = su[SERVEUR[k]] ?? null;
    const comparable = k === 'kg' ? kgVersG(v) : v;
    if (comparable === serveur) { egaux.push(k); continue; }
    if (Object.prototype.hasOwnProperty.call(acquis, CORPS[k]) && acquis[CORPS[k]] === v) continue;
    if (k === 'sscc' && v && serveurUnits.some((x) => x.id !== u.id && x.sscc === v)) { differes.push(k); continue; }
    body[CORPS[k]] = v;
  }
  return { body, differes, egaux };
}

// ---------- En-tête : mêmes champs « sales » que les fiches ----------
// st.salesEntete : 'ord' (réf. de chargement) | 'fmt' (format palette, longueur, largeur). Un
// appareil resté sur d'anciennes valeurs ne défait jamais la correction d'un autre : seul ce qui
// a été changé ICI part ; le reste prend la valeur du serveur.
export function salirEntete(st, k) {
  if (!st.salesEntete) st.salesEntete = new Set();
  st.salesEntete.add(k);
}
export const enteteSale = (st, k) => !!st.salesEntete && st.salesEntete.has(k);
const memeFormat = (st, dto) => {
  if (st.format !== 'AUTRE') return st.format === dto.palletFormat;
  const f = formatEtat(st);
  return f.ok && dto.palletFormat === 'AUTRE' && dto.palletLengthCm === f.l && dto.palletWidthCm === f.w;
};

// Corps du PATCH de l'en-tête : la réf. de chargement (champ local `ord`, seul identifiant —
// figée après le 1er mail, côté serveur aussi) et le format palette (select `fmt`, `fl`, `fw`),
// seulement s'ils ont été changés SUR CET APPAREIL. Rend { body, egaux } (egaux : champs sales
// déjà identiques au serveur → ils redeviennent propres).
export function diffEntete(st, dto, { exclure = [], acquis = {} } = {}) {
  const body = {}, egaux = [];
  const deja = (cle, v) => Object.prototype.hasOwnProperty.call(acquis, cle) && acquis[cle] === v;
  if (!dto) return { body, egaux };
  // Jamais vide (obligatoire) : seulement une réf. juste (le PDA a déjà affiché le message).
  if (enteteSale(st, 'ord') && !dto.lockedAt && !exclure.includes('ord') && !verifRefChargement(st.ord)) {
    const v = normRefChargement(st.ord);
    if (v === dto.orderRef) egaux.push('ord');
    else if (!deja('orderRef', v)) body.orderRef = v;
  }
  if (enteteSale(st, 'fmt')) {
    if (memeFormat(st, dto)) egaux.push('fmt');
    else if (st.format === 'AUTRE') {
      // Longueur / largeur : envoyées ensemble, une fois justes et le champ quitté.
      const f = formatEtat(st);
      if (f.ok && !exclure.includes('fl') && !exclure.includes('fw')) {
        const memeAcquis = deja('palletFormat', 'AUTRE') && deja('palletLengthCm', f.l) && deja('palletWidthCm', f.w);
        if (!memeAcquis) Object.assign(body, { palletFormat: 'AUTRE', palletLengthCm: f.l, palletWidthCm: f.w });
      }
    } else if (!deja('palletFormat', st.format)) {
      body.palletFormat = st.format;   // EUR / IND : longueur × largeur fixées par le serveur
    }
  }
  return { body, egaux };
}
// Le serveur a enregistré `corps` : les champs redeviennent propres — sauf retouchés depuis l'envoi.
export function enteteEnregistre(st, corps) {
  if (!st.salesEntete) return;
  if ('orderRef' in corps && normRefChargement(st.ord) === corps.orderRef) st.salesEntete.delete('ord');
  if ('palletFormat' in corps && memeFormat(st, corps)) st.salesEntete.delete('fmt');
}
// Format palette du serveur → champs de l'écran (select, longueur, largeur).
function formatDepuisServeur(st, dto) {
  st.format = ['EUR', 'IND', 'AUTRE'].includes(dto.palletFormat) ? dto.palletFormat : 'EUR';
  st.fl = cmTexte(dto.palletLengthCm ?? 120);
  st.fw = cmTexte(dto.palletWidthCm ?? 80);
}
// Champs PROPRES de l'en-tête : valeurs du serveur (corrigées sur un autre appareil). Après le 1er
// mail, la réf. du serveur fait foi même si elle a été retouchée ici (verrouillée) : la frappe et
// son éventuel refus (« … a déjà sa commande ») disparaissent — le champ n'est plus modifiable.
// `refFigee` (revue 17/10) : la réf. est figée POUR CET ÉCRAN sans être verrouillée — commande de test,
// sans le droit commandes_pretes.admin.all (l'écran le sait, pas cette fonction) : même chose, sinon une
// réf. en attente, refusée ou invalide bloquerait l'envoi derrière un champ désactivé.
// (Avec `refFigee`, le client est figé aussi : son refus et son lien « Ouvrir cette commande » partent.)
export function adopterEntete(st, dto, { refFigee = false } = {}) {
  if (dto.lockedAt || refFigee) {
    if (st.salesEntete) st.salesEntete.delete('ord');
    if (st.refusEntete) delete st.refusEntete.ord;
    if ('lienExistant' in st) st.lienExistant = null;
  }
  if (refFigee) {
    if ('refusClient' in st) st.refusClient = null;
    if ('lienExistantClient' in st) st.lienExistantClient = null;
  }
  if (!enteteSale(st, 'ord')) st.ord = dto.orderRef || '';
  if (!enteteSale(st, 'fmt')) formatDepuisServeur(st, dto);
}

// ---------- Du serveur vers l'écran ----------
export function ficheDepuisServeur(st, su) {
  const u = {
    id: su.id, type: su.kind, sscc: su.sscc || '', ssccAt: 0,
    h: cmTexte(su.heightCm), l: cmTexte(su.lengthCm), w: cmTexte(su.widthCm), kg: gVersKg(su.weightG),
    photos: 0, photoIds: [], sales: new Set(),
  };
  // Un SSCC déjà enregistré est la référence de la fiche (comme les fiches d'exemple de la maquette).
  if (su.sscc) { u.ssccAt = ++st.tic; u.ref = { sscc: su.sscc, at: u.ssccAt }; u.valide = true; }
  return u;
}

// Champs PROPRES d'une fiche déjà à l'écran : ils prennent la valeur du serveur (saisie sur un
// autre appareil, ou normalisée par le serveur). Les champs sales gardent la frappe.
export function adopterServeur(st, u, su) {
  for (const k of champs(u)) {
    if (estSale(u, k)) continue;
    const serveur = su[SERVEUR[k]] ?? null;
    const local = vouluChamp(u, k);
    if (local !== INVALIDE && (k === 'kg' ? kgVersG(local) : local) === serveur) continue;
    u[k] = k === 'kg' ? gVersKg(serveur) : cmTexte(serveur);
    if (u.refus) delete u.refus[k];
  }
  if (!estSale(u, 'sscc') && normSscc(u.sscc) !== (su.sscc || '')) {
    u.sscc = su.sscc || '';
    if (u.refus) delete u.refus.sscc;
    if (su.sscc) { u.ssccAt = ++st.tic; u.ref = { sscc: su.sscc, at: u.ssccAt }; u.valide = true; } else { delete u.ref; u.ssccAt = 0; u.valide = false; }
  }
}

export function enteteDepuisServeur(st, dto) {
  st.ord = dto.orderRef || '';   // la réf. de chargement (ODP)
  formatDepuisServeur(st, dto);
}

// Une fiche retirée de l'écran (✕, « − », ou disparue du serveur) : ses états d'enregistrement,
// ses photos en attente de renvoi et son SSCC en attente partent avec elle — sinon l'envoi
// resterait bloqué à vie sur « saisies non enregistrées ».
export function oublierFiche(st, uid) {
  if (st.sauv) st.sauv.delete(uid);
  if (st.photosEchec) st.photosEchec.delete(uid);
  if (st.attente) st.attente.delete(uid);
}
// ✕ / « − » : la fiche quitte l'écran tout de suite (oubliée), mais on garde de quoi la remettre
// TELLE QU'ELLE si le serveur refuse la suppression (envoi en cours, remplie ailleurs, réseau) :
// frappes non enregistrées, photos à renvoyer, état d'enregistrement.
export function retirerFiche(st, u) {
  const garde = {
    u, i: st.units.indexOf(u),
    sauv: st.sauv && st.sauv.get(u.id), photosEchec: st.photosEchec && st.photosEchec.get(u.id), attente: !!(st.attente && st.attente.has(u.id)),
  };
  if (garde.i >= 0) st.units.splice(garde.i, 1);
  if (st.supprimees) st.supprimees.add(u.id);
  oublierFiche(st, u.id);
  return garde;
}
// Suppression refusée : à rappeler AVANT d'appliquer la réponse du serveur (la fusion garde alors
// la même fiche, ses champs sales et son état). Les photos prises en route vers la suppression
// (u.photosSuspendues, jamais envoyées) rejoignent les photos à renvoyer : « touchez pour réessayer ».
// (Les champs sales, eux, n'ont pas été envoyés non plus : la page relance leur enregistrement.)
export function remettreFiche(st, g) {
  const u = g.u;
  if (st.supprimees) st.supprimees.delete(u.id);
  if (!st.units.includes(u)) st.units.splice(Math.min(Math.max(g.i, 0), st.units.length), 0, u);
  if (g.sauv && st.sauv) st.sauv.set(u.id, g.sauv);
  if (g.photosEchec && st.photosEchec) st.photosEchec.set(u.id, g.photosEchec);
  if (g.attente && st.attente) st.attente.add(u.id);
  if (u.photosSuspendues && u.photosSuspendues.length && st.photosEchec) {
    st.photosEchec.set(u.id, [...(st.photosEchec.get(u.id) || []), ...u.photosSuspendues]);
    u.photosSuspendues = [];
    if (st.sauv) {
      const s = st.sauv.get(u.id) || { n: 0, erreur: null, ok: false };
      s.erreur = s.erreur || 'photo non envoyée (suppression refusée)';
      s.ok = false;
      st.sauv.set(u.id, s);
    }
  }
}
// Saisies restées en route (réseau), sur les fiches PRÉSENTES seulement, ou en-tête.
export const saisiesNonEnregistrees = (st) => st.units.some((u) => (st.sauv && st.sauv.get(u.id)?.erreur) || (st.photosEchec && st.photosEchec.has(u.id)))
  || !!(st.sauvEntete && st.sauvEntete.erreur);

// Réponse du serveur → fiches de l'écran. Les champs SALES des fiches déjà à l'écran gardent ce
// qui a été tapé (une frappe a pu suivre l'enregistrement) ; les champs propres et les photos
// viennent du serveur. Les fiches supprimées à l'écran dont la suppression n'est pas encore passée
// restent cachées, de même que les photos retirées (« − ») pas encore effacées sur le serveur. Les
// photos en cours d'envoi (u.photosEnCours) comptent déjà : la fiche n'attend pas le réseau pour avancer.
// Les fiches qui ne sont plus à l'écran sont oubliées (oublierFiche).
export function fusionner(st, dto, supprimees = new Set(), photosMasquees = new Set()) {
  const anciennes = new Map(st.units.map((u) => [u.id, u]));
  const rang = (su) => (su.kind === 'PAL' ? 0 : 1);   // palettes d'abord, puis colis
  const serveur = [...(dto.units || [])].sort((a, b) => rang(a) - rang(b) || (a.position ?? 0) - (b.position ?? 0));
  st.units = serveur.filter((su) => !supprimees.has(su.id)).map((su) => {
    const ancienne = anciennes.get(su.id);
    const u = ancienne || ficheDepuisServeur(st, su);
    u.type = su.kind;
    u.loadedAt = su.loadedAt || null;   // v2 : chargée dans un camion (étiquette et suppression figées)
    if (ancienne) adopterServeur(st, u, su);
    u.photoIds = (su.photos || []).map((p) => p.id).filter((pid) => !photosMasquees.has(pid));
    u.photos = u.photoIds.length + (u.photosEnCours || 0);
    return u;
  });
  const presentes = new Set(st.units.map((u) => u.id));
  for (const uid of new Set([...(st.sauv ? st.sauv.keys() : []), ...(st.photosEchec ? st.photosEchec.keys() : []), ...(st.attente || [])])) {
    if (!presentes.has(uid)) oublierFiche(st, uid);
  }
  st.dto = dto;
}

// Les routes renvoient soit le CommandeDTO, soit { dto, … } (envoi, ajout d'unité).
export const extraireDto = (r) => (r && Array.isArray(r.units) ? r : r && r.dto && Array.isArray(r.dto.units) ? r.dto : null);

// Écriture refusée parce que la commande est FIGÉE (envoi en cours / à vérifier) : ce n'est pas
// une valeur fausse — la saisie reste à l'écran, « non enregistrée — touchez pour réessayer ».
// (+ REESSAYER : un envoi est passé entre la lecture et l'écriture — la valeur n'est pas fausse.)
export const ENVOI_FIGE = Object.freeze(['ENVOI_EN_COURS', 'ENVOI_A_VERIFIER', 'REESSAYER']);
export const refusEnvoiFige = (data) => !!data && ENVOI_FIGE.includes(data.error);
